import { dayAxis, weekAxis } from '@/components/charts/generic';
import type { FeedingFallthroughDay, FeedingResolution } from '@/domain/feedingDashboard';

export interface FallthroughTimelinePoint {
    /** Bucket start: a day, or the Monday of an ISO week. */
    day: string;
    /** Null for a bucket with no observed pellets, drawn as a gap rather than as 0 %. */
    value: number | null;
}

/**
 * The pellet timeline's bars: one per bucket from the first to the last bucket the
 * backend sent, in date order, with every bucket in between present.
 *
 * Buckets without pellets stay on the axis as empty slots. Dropping them put the bars
 * either side of a gap next to each other, which reads as consecutive days (IPS 3.2:
 * every date within the period). A bucket the backend did not send is filled the same
 * way, so the axis does not depend on the backend padding it.
 */
export function fallthroughTimelinePoints(
    days: FeedingFallthroughDay[],
    resolution: FeedingResolution
): FallthroughTimelinePoint[] {
    // The backend emits one row per bucket, but collapse defensively so a future
    // per-operator split cannot silently plot two bars on the same key.
    const byDay = new Map<string, { sum: number; count: number }>();
    for (const entry of days) {
        if (entry.fallthroughPct == null) continue;
        const agg = byDay.get(entry.day) ?? { sum: 0, count: 0 };
        agg.sum += entry.fallthroughPct;
        agg.count += 1;
        byDay.set(entry.day, agg);
    }

    // Weekly buckets are keyed by their Monday; filling them by day would put seven
    // identical week labels on the axis for one missing week.
    const axis = (resolution === 'week' ? weekAxis : dayAxis)(days, (entry) => entry.day);
    return axis.map((day) => {
        const agg = byDay.get(day);
        return { day, value: agg ? agg.sum / agg.count : null };
    });
}
